import React, { useCallback } from 'react';

import { CheckMarkCircle2, CircleOutline } from '@lyearn/icons';
import { cn } from '@lyearn/style';
import { Box, Stack, Typography } from '@lyearn/ui';

import { SETTING_TAB_CLASSES } from './consts';
import { SettingsTabType } from './types';

interface SettingTabProps {
  tab: SettingsTabType;
  isSelected: boolean;
  direction?: 'col' | 'row';
  size: 'medium' | 'large' | 'x-large' | 'xx-large';
  onChange: (val: string) => void;
}

function SettingTab(props: SettingTabProps) {
  const { tab, isSelected, direction, size, onChange } = props;
  const { Icon, label, id, description } = tab;
  const classes = SETTING_TAB_CLASSES[size];

  const onSelect = useCallback(() => onChange(id), [onChange, id]);

  return (
    <Stack
      alignItems="center"
      className={cn(
        'flex-shrink-0 border',
        classes.root,
        isSelected ? 'border-interactive-primary' : 'border-interactive-secondary-hover',
        { 'flex-1': direction === 'row' },
      )}
      justifyContent="between"
      role="button"
      tabIndex={0}
      onClick={onSelect}>
      <Stack alignItems="center" className={classes.container}>
        {Icon ? (
          <Icon
            className={classes.icon}
            fillColorToken={isSelected ? 'icon-active' : 'icon-primary'}
          />
        ) : null}
        <Box className="space-y-4">
          <Typography variant={classes.label}>{label}</Typography>
          {description ? (
            <Typography color="text-text-secondary" variant="body-short-02">
              {description}
            </Typography>
          ) : null}
        </Box>
      </Stack>
      {isSelected ? (
        <CheckMarkCircle2 className={classes.checkmarkIcon} fillColorToken="icon-active" />
      ) : (
        <CircleOutline className={classes.circleIcon} fillColorToken="bg-tertiary" />
      )}
    </Stack>
  );
}

export default React.memo(SettingTab);
